import axios from 'axios';
import 'babel-polyfill';
import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import AppointmentInfo from './appointmentinfo';
import FormWrapper, { FormSelect, FormSubmit, FormTextField } from './formwrapper';
import { Appointment, BookingInfo } from './interfaces';
import { labels } from './labels';
import { $disable, $enable, handleInverseRegexInput, redirectToError, useSearchParams } from './util';

interface IProps {
    name: string;
    scheduleDate: Date
}

const PersonalInfo = (props: IProps) => {
    const history = useHistory();
    const [npi, start, apptType, deptid, backto, reason] = useSearchParams(["npi", "start", "apptType", "deptid", "backto", "reason"]);
    const [appt, setAppt] = useState<Appointment>();
    const [dateOfBirth, setDateOfBirth] = useState<string>("");
    const [zip, setZip] = useState<string>("");
    const [otherLanguageVisible, setOtherLanguageVisible] = useState<boolean>(false);
    const [personalInfo, setPersonalInfo] = useState<BookingInfo>(new BookingInfo());
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [address1, setAddress1] = useState("");
    const [address2, setAddress2] = useState("");
    const [city, setCity] = useState("");

    useEffect(() => {
        axios.get(`api/appointments?npi=${npi}&start=${start}&deptid=${deptid}`)
            .then((result) => {
                if (result.data) {
                    setAppt(result.data);
                    getPersonalInfo();
                } else {
                    history.push('/');
                }
            })
    }, [])

    function getPersonalInfo() {
        axios.get(`api/appointments/GetPersonalInfo`)
            .then((result) => {
                setPersonalInfo(result.data);
                setDateOfBirth(result.data.dateOfBirth);
                setZip(result.data.zip);
                setFirstName(result.data.firstName);
                setLastName(result.data.lastName);
                setAddress1(result.data.address1);
                setAddress2(result.data.address2);
                setCity(result.data.city);
                document.getElementById('firstName').focus();
            })
    }

    function savePersonalInfo(e: React.MouseEvent<HTMLElement>) {        
        e.preventDefault();        
        $disable("verifyButton");        

        var fields = [
            "firstName", "lastName", "dateOfBirth", "gender", "legalSex", "preferredLanguage", "otherLanguage",
            "address1", "address2", "city", "state", "zip"          
        ]
        var info = { npi, startTime: start, departmentId: deptid, apptType, visitReason: reason } as BookingInfo;               

        try {
            FormSubmit("UPDATE_PERSONAL_INFO", `api/appointments/SavePersonalInfo`, fields, info,
                (response) => {
                    $enable("verifyButton");
                    history.push(`/contactinfo${window.location.search}`);
                },
                (result) => {                    
                    $enable("verifyButton");
                },
                (result) => {
                    if (result && result.error) {
                        history.push(`contactprovider?npi=${npi}&deptid=${deptid}&error=${encodeURIComponent(result.error)}`)
                    } else {
                        console.log(JSON.stringify(result));
                        redirectToError(window.location.search, "UNEXPECTED_ERROR");
                        //$enable("verifyButton");
                    }
                },
                { "AGE_CUTOFF": labels.personalinfo.ageCutoff("" + appt.ageCutoff) }
            );
        } catch (ex) {
            console.log(JSON.stringify(ex));
            redirectToError(window.location.search, "UNEXPECTED_ERROR");
            //$enable("verifyButton");
        }
    }

    if (!appt || !personalInfo) return (<div></div>)

    return (
        <FormWrapper currentStep={2}>
            {/*<FormProgress currentStep={2} />*/}
            <AppointmentInfo appt={appt} apptType={apptType} />
                        
            <h2 className="mb-3 mt-3">{labels.personalinfo.personalInformation}</h2>
            <div className="row">
                <FormTextField id="firstName" label={labels.personalinfo.firstName} placeholder={labels.personalinfo.firstName} value={firstName} maxLength={50} required onChange={(e) => handleInverseRegexInput(e, setFirstName, /[^a-zA-Z\- ]/)} />
                <FormTextField id="lastName" label={labels.personalinfo.lastName} placeholder={labels.personalinfo.lastName} value={lastName} maxLength={50} required onChange={(e) => handleInverseRegexInput(e, setLastName, /[^a-zA-Z\- ]/)} />
            </div>
            <div className="row">
                <FormTextField className="col-md-3 mb-3 dobField" id="dateOfBirth" label={labels.personalinfo.dob} placeholder={labels.personalinfo.dobPlaceHolder} value={dateOfBirth} mask="date" onChange={(e) => setDateOfBirth(e.target.value)} required />
            </div>
            <div className="row">
                <FormSelect id="gender" label={labels.personalinfo.genderIdentity} options={appt.formOptions.genderIdentity} defaultValue={personalInfo.gender} />
                <FormSelect className="col-md-3 mb-3" id="legalSex" label={labels.personalinfo.legalSex} options={appt.formOptions.legalSex} defaultValue={personalInfo.legalSex} required />                
            </div>
            <div className="row">
                <FormSelect className="col-md-3 mb-3" id="preferredLanguage" label={labels.personalinfo.preferredLanguage} hideChoose={true}
                    options={[...appt.formOptions.preferredLanguage, { name: labels.personalinfo.other, value: "Other" }]} defaultValue={personalInfo.preferredLanguage || "eng" } required
                    onChange={(e) => { setOtherLanguageVisible(e.currentTarget.value === "Other") }} />
                <FormTextField className={"col-md-6 mb-3" + (!otherLanguageVisible && " d-none")} id="otherLanguage" label={labels.personalinfo.otherLanguage} placeholder={labels.personalinfo.otherLanguagePlaceholder} defaultValue={personalInfo.otherLanguage} maxLength={50} required />
            </div>

            <FormTextField className="mb-3" id="address1" label={labels.personalinfo.address1} placeholder={labels.personalinfo.address1Placeholder} value={address1} required maxLength={100} onChange={(e) => handleInverseRegexInput(e, setAddress1, /[^0-9a-zA-Z\-\.\/ ]/)} />
            <FormTextField className="mb-3" id="address2" label={labels.personalinfo.address2} placeholder={labels.personalinfo.address2Placeholder} value={address2} maxLength={100} onChange={(e) => handleInverseRegexInput(e, setAddress2, /[^0-9a-zA-Z\-\. ]/)} />

            <div className="row">
                <FormTextField className="col-md-5 mb-3" id="city" label={labels.personalinfo.city} placeholder={labels.personalinfo.city} value={city} required maxLength={50} onChange={(e) => handleInverseRegexInput(e, setCity, /[^a-zA-Z\-\. ]/)} />
                <FormSelect className="col-md-2 mb-3" id="state" label={labels.personalinfo.state} options={appt.formOptions.state} defaultValue={personalInfo.state || "MA"} required />
                <FormTextField className="col-md-5 mb-3" id="zip" label={labels.personalinfo.zipcode} placeholder={labels.personalinfo.zipcodePlaceholder} value={zip} required maxLength={5} onChange={(e) => setZip(e.target.value)} mask="zip" />
            </div>

            <hr className="mb-4" />

            <div className="row">
                <div className="col-md mb-4">
                    <button id="verifyButton" className="btn btn-primary btn-block" type="submit" onClick={savePersonalInfo}>{labels.personalinfo.continue}</button>
                </div>
                <div className="col-md mb-4">
                    <Link className="btn btn-outline-primary btn-block" to={{ pathname: "/provider", search: window.location.search }}>{labels.personalinfo.backTo}</Link>
                </div>
            </div>
        </FormWrapper>
    );
}

export default PersonalInfo;